import { SaveIcon } from '@systemeio/ui/icons'
import { BreadcrumbsData, ContactSlugEnum, FormSelect, getLocaleLanguage, LocaleEnum, PrimaryButton, toast } from '@systemeio/ui'
import { BadRequest, InternalError } from '@systemeio/util'
import { createContact } from 'modules/contacts/api/contact-api'
import { contactDefaultErrors } from 'modules/contacts/constants/contact-default-value-error'
import ContactAddField from 'modules/contacts/contact-page/components/contact-add-field'
import ContactFieldSkeleton from 'modules/contacts/contact-page/components/contact-field-skeleton'
import ContactFields from 'modules/contacts/contact-page/components/contact-fields'
import ContactSelectTags from 'modules/contacts/contact-page/components/contact-select-tags'
import { ContactPageLoco } from 'modules/contacts/contact-page/constants/contact-page-loco'
import { useContactFields } from 'modules/contacts/hooks/use-contact-fields'
import { ContactErrorsInterface } from 'modules/contacts/types/contact-errors-interface'
import { ContactBulkApiInterface } from 'modules/contacts/types/contact-menu-interface'
import { NextPage } from 'next'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import BreadcrumbsDashboard from 'shared/components/breadcrumbs'
import { FieldErrorAndDescription } from 'shared/components/form/field-error-and-description'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { LocaleDataInterface } from 'shared/types/locale-data-interface'
import { isValidEmail } from 'shared/utils/email-validation'
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props'
import { useRevalidateModules } from 'shared/hooks/use-revalidate-modules'
import { RequestModuleEnum } from 'shared/enums/request-module-enum'

const ContactCreate: NextPage = () => {
  const router = useRouter()
  const { t } = useLocoTranslation()
  const { user } = useUser()
  const { fields, mutateFields } = useContactFields()
  const { revalidateModules } = useRevalidateModules()

  const [tempState, setTempState] = useState<Record<string, string | undefined>>()
  const [tempTags, setTempTags] = useState<ContactBulkApiInterface[] | undefined>([])
  const [isSaving, setIsSaving] = useState(false)

  const resetTags = () => setTempTags([])

  const [errors, setErrors] = useState<ContactErrorsInterface>(contactDefaultErrors)

  useEffect(() => {
    if (fields && user) {
      const fieldValues = fields.items.reduce((obj, el) => {
        return { ...obj, [el.slug]: el.slug === ContactSlugEnum.Country ? undefined : '' }
      }, {})
      setTempState({ ...fieldValues, ['language']: user.dashboardLocale })
    }
  }, [fields, user])

  const breadcrumbsData: BreadcrumbsData[] = [
    {
      caption: t('dashboard.contact_page.title_plural'),
      href: '/contacts',
      isStatic: true,
    },
    { caption: t('dashboard.contact_page.craete_contact') },
  ]

  const localeData: LocaleDataInterface[] = Object.values(LocaleEnum).map(locale => ({
    id: locale as LocaleEnum,
    caption: getLocaleLanguage(locale),
  }))

  const onSaveContact = async () => {
    try {
      setErrors(contactDefaultErrors)
      setIsSaving(true)
      if (fields && tempState && tempTags) {
        if (!isValidEmail(tempState[ContactSlugEnum.Email] ?? '')) {
          setIsSaving(false)

          const emailFieldIndex = fields.items.findIndex(
            field => field.slug === ContactSlugEnum.Email,
          )

          return setErrors(prevState => ({
            ...prevState,
            fields: { [emailFieldIndex]: { value: [t('dashboard.validation.email_not_valid')] } },
          }))
        }
        await createContact({
          tags: tempTags.map(el => el.id),
          locale: tempState['language'] as LocaleEnum,
          fieldValues: fields.items.map(field => ({
            contactField: Number(field.id),
            value: tempState[field.slug] ?? '',
          })),
        })
      }
      setIsSaving(false)
      await revalidateModules([RequestModuleEnum.Contacts], true)
      await router.push('/contacts')
      toast.success(t('dashboard.contact_page.created'))
    } catch (e) {
      setIsSaving(false)
      if (e instanceof BadRequest) {
        const contactErrors = e.errors
        setErrors(prevState => ({ ...prevState, fields: contactErrors.fields.fieldValues }))
        if (e.errors.common) {
          const common = contactErrors.common
          setErrors(prevState => ({ ...prevState, common: common }))
          toast.error(common.join(''))
        }
      } else if (e instanceof InternalError) {
        toast.error(t('core.error.internal_error_message'))
      } else {
        toast.error(t('global.error'))
      }
    }
  }

  return (
    <>
      <div className={`flex flex-col gap-5 lg:px-28`}>
        <div className="flex items-center justify-between gap-5">
          <BreadcrumbsDashboard data={breadcrumbsData} />
          <div className={'flex gap-3'}>
            <PrimaryButton
              isFetching={isSaving}
              onClick={() => onSaveContact()}
              disabled={
                !tempState ||
                (tempState &&
                  (tempState[ContactSlugEnum.Email] === '' || tempState['language'] === undefined))
              }
            >
              <SaveIcon />
              <span className={'hidden lg:flex'}>{t('global.save')}</span>
            </PrimaryButton>
          </div>
        </div>
        <div className={'bg-background-secondary flex flex-col gap-5 rounded-lg p-5'}>
          {tempState && fields ? (
            <div className={'flex flex-col gap-5'}>
              <div
                className={
                  'flex w-full flex-col flex-wrap items-end gap-y-5 lg:-ml-5 lg:w-[calc(100%+20px)] lg:flex-row'
                }
              >
                {fields.items.map((field, index) => (
                  <ContactFields
                    type={'create'}
                    mutateFields={mutateFields}
                    value={tempState[field.slug]}
                    className={'w-full sm:basis-1/2 lg:pl-5'}
                    key={field.id}
                    field={field}
                    error={
                      errors?.fields && errors.fields[index]
                        ? errors?.fields[index]?.value.join('')
                        : ''
                    }
                    onChange={value => setTempState(prev => ({ ...prev, [field.slug]: value }))}
                  />
                ))}
                <FormSelect
                  required
                  withoutCloseIcon
                  label={t('global.language')}
                  placeholder={t('global.language')}
                  containerClassName={'w-full sm:basis-1/2 lg:pl-5'}
                  data={localeData}
                  value={tempState['language'] as LocaleEnum}
                  onChange={data => setTempState(prev => ({ ...prev, ['language']: data }))}
                />
              </div>
              <ContactAddField disabled={!fields} mutateFields={mutateFields} />
            </div>
          ) : (
            <ContactFieldSkeleton />
          )}
          <ContactSelectTags tempTags={tempTags} setTempTags={setTempTags} resetTags={resetTags} />
          <FieldErrorAndDescription error={errors.common.join('')} errorClassName={'text-center'} />
        </div>
      </div>
    </>
  )
}

export const getServerSideProps = getBaseServerSideProps(
  ContactPageLoco,
  'header.top_menu.contacts',
)
export default ContactCreate
